import { useState, useEffect } from 'react';
import { AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import {
    Loader2, Plane, Files, Users, Calendar,
    MapPin, DollarSign, CheckCircle2, XCircle,
    AlertCircle, Building2, Clock, Truck, Ship,
    Target, File,
    ArrowLeft
} from 'lucide-react';
import {
    Breadcrumb, BreadcrumbItem, BreadcrumbLink,
    BreadcrumbList, BreadcrumbPage, BreadcrumbSeparator
} from '@/components/ui/breadcrumb';
import { Link } from '@tanstack/react-router';
import { toast } from 'react-toastify';
import { AdminTravelsService, type TravelDocument } from '@/lib/collections/requestsTravels/rrhh/getRequestTravelRrhh';
import { countries, ecuadorProvinces } from '../utils/countrys-provinces';
import { fetchCompanies, type Company } from '@/lib/collections/organizations/getOrganizationsFetch';
import { useUsersMap } from '@/lib/collections/requestsTravels/rrhh/useCollaboratorsMap';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/context/AuthContext';

const COUNTRY_MAP = Object.fromEntries(countries.map(country => [country.value, country.label]));
const PROVINCE_MAP = Object.fromEntries(ecuadorProvinces.map(province => [province.value, province.label]));
const getCountryLabel = (value: string) => COUNTRY_MAP[value] || value;
const getProvinceLabel = (value: string) => PROVINCE_MAP[value] || value;

interface Props { travelId: string; }

const DetailUserTravel = ({ travelId }: Props) => {
    const { user } = useAuth();
    const [travel, setTravel] = useState<TravelDocument | null>(null);
    const [loading, setLoading] = useState(true);
    const [companiesMap, setCompaniesMap] = useState<Map<string, string>>(new Map());
    const { getUserName } = useUsersMap();

    useEffect(() => {
        const fetchData = async () => {
            try {
                const [doc, companiesData] = await Promise.all([
                    AdminTravelsService.getTravelById(travelId),
                    fetchCompanies()
                ]);
                setTravel(doc);
                const compMap = new Map<string, string>();
                companiesData.forEach((c: Company) => compMap.set(c.$id, c.name));
                setCompaniesMap(compMap);
            } catch (error) {
                console.error(error);
                toast.error('No se pudo cargar el viaje');
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, [travelId]);

    const getCompanyName = (id: string) => companiesMap.get(id) || id;
    const formatDate = (d: string) => new Date(d).toLocaleDateString('es-EC', { day: '2-digit', month: 'short', year: 'numeric' });
    const formatTime = (d: string) => new Date(d).toLocaleTimeString('es-EC', { hour: '2-digit', minute: '2-digit' });

    const getStatusConfig = (status: string) => {
        const map = {
            pending: { color: 'bg-amber-100 text-amber-800 border-amber-300 dark:bg-amber-900/30 dark:text-amber-300 dark:border-amber-700', label: 'Pendiente', icon: AlertCircle, dot: 'bg-amber-500' },
            approved: { color: 'bg-emerald-100 text-emerald-800 border-emerald-300 dark:bg-emerald-900/30 dark:text-emerald-300 dark:border-emerald-700', label: 'Aprobado', icon: CheckCircle2, dot: 'bg-emerald-500' },
            rejected: { color: 'bg-rose-100 text-rose-800 border-rose-300 dark:bg-rose-900/30 dark:text-rose-300 dark:border-rose-700', label: 'Rechazado', icon: XCircle, dot: 'bg-rose-500' },
        };
        return map[status as keyof typeof map] || map.pending;
    };

    const getTransportInfo = (type: string) => {
        if (type === 'air') return { label: 'Aéreo', icon: <Plane className="h-4 w-4" /> };
        if (type === 'sea') return { label: 'Marítimo', icon: <Ship className="h-4 w-4" /> };
        return { label: 'Terrestre', icon: <Truck className="h-4 w-4" /> };
    };

    if (loading) return (
        <div className="flex items-center justify-center py-24">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
    );

    if (!travel) return (
        <div className="flex flex-col items-center justify-center py-24 gap-3">
            <div className="rounded-full bg-muted p-6">
                <Plane className="h-10 w-10 text-muted-foreground" />
            </div>
            <p className="text-lg font-medium">No se encontró el viaje</p>
        </div>
    );

    const statusConfig = getStatusConfig(travel.statusByRrhh);
    const StatusIcon = statusConfig.icon;
    const transport = travel.transportationType ? getTransportInfo(travel.transportationType) : null;

    return (
        <>
            <Breadcrumb>
                <BreadcrumbList>
                    <BreadcrumbItem>
                        <div className="flex items-center gap-1.5">
                            <Files className="h-4 w-4" />
                            <BreadcrumbPage>Solicitudes</BreadcrumbPage>
                        </div>
                    </BreadcrumbItem>
                    <BreadcrumbSeparator />
                    <BreadcrumbItem>
                        <BreadcrumbLink asChild><Link to="/requests">Menu</Link></BreadcrumbLink>
                    </BreadcrumbItem>
                    <BreadcrumbSeparator />
                    <BreadcrumbItem>
                        <BreadcrumbLink asChild>
                            <Link to="/requests/$id/travels" params={{ id: user?.$id ?? '' }}>
                               Viajes
                            </Link>
                        </BreadcrumbLink>
                    </BreadcrumbItem>
                    <BreadcrumbSeparator />
                    <BreadcrumbItem>
                        <BreadcrumbPage><File /></BreadcrumbPage>
                    </BreadcrumbItem>
                </BreadcrumbList>
            </Breadcrumb>

            <div className="pt-6 w-full space-y-4 font-outfit pb-16">

                <Card className="shadow-sm overflow-hidden">

                    {/* Header */}
                    <CardContent className="px-6 py-6 border-b">
                        <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4">
                            <div className="flex items-start gap-4">
                                <div className="rounded-2xl bg-primary/10 border border-primary/20 p-3 shrink-0">
                                    <Plane className="h-6 w-6 text-primary" />
                                </div>
                                <div>
                                    <h1 className="text-xl font-bold tracking-tight">{travel.title}</h1>
                                    <p className="text-sm text-muted-foreground mt-1">
                                        Solicitud registrada el{' '}
                                        <span className="font-medium text-foreground">{formatDate(travel.$createdAt)}</span>
                                        {' '}a las {formatTime(travel.$createdAt)}
                                    </p>
                                    <div className="flex items-center gap-2 mt-2">
                                        <Users className="h-3.5 w-3.5 text-muted-foreground" />
                                        <span className="text-sm text-muted-foreground">{getUserName(travel.userId)}</span>
                                        {travel.isGroup && travel.usersId.length > 1 && (
                                            <span className="text-xs text-muted-foreground">
                                                +{travel.usersId.length - 1} colaborador(es)
                                            </span>
                                        )}
                                    </div>
                                </div>
                            </div>
                            <Badge className={`${statusConfig.color} border flex items-center gap-2 px-3 py-1.5 text-sm font-medium shrink-0 w-fit`}>
                                <span className={`h-2 w-2 rounded-full ${statusConfig.dot} animate-pulse`} />
                                <StatusIcon className="h-3.5 w-3.5" />
                                {statusConfig.label}
                            </Badge>
                        </div>
                    </CardContent>

                    {/* Colaboradores */}
                    {travel.isGroup && travel.usersId.length > 1 && (
                        <CardContent className="px-6 py-4 border-b bg-muted/30">
                            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-widest mb-2.5">
                                Colaboradores del viaje
                            </p>
                            <div className="flex flex-wrap gap-2">
                                {travel.usersId.map(id => (
                                    <Badge key={id} variant="secondary" className="gap-1.5 font-normal">
                                        <Users className="h-3 w-3" />
                                        {getUserName(id)}
                                    </Badge>
                                ))}
                            </div>
                        </CardContent>
                    )}

                    {/* Objetivo */}
                    <CardContent className="px-6 py-5 border-b">
                        <div className="flex items-center gap-2 mb-3">
                            <Target className="h-4 w-4 text-primary" />
                            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-widest">
                                Objetivo del viaje
                            </p>
                        </div>
                        <p className="text-sm leading-relaxed whitespace-pre-wrap text-foreground">
                            {travel.tripPurpose || 'Sin objetivo especificado'}
                        </p>
                    </CardContent>

                    {/* Fechas + Destino */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 border-b">
                        <CardContent className="px-6 py-5 border-b sm:border-b-0 sm:border-r">
                            <div className="flex items-center gap-2 mb-3">
                                <Calendar className="h-4 w-4 text-primary" />
                                <p className="text-xs font-semibold text-muted-foreground uppercase tracking-widest">Fechas</p>
                            </div>
                            <div className="space-y-2">
                                <div className="flex items-center gap-3">
                                    <div className="flex items-center gap-1.5 text-xs text-muted-foreground w-12">
                                        <Clock className="h-3 w-3" /> Inicio
                                    </div>
                                    <span className="text-sm font-medium">{formatDate(travel.startDate)}</span>
                                    <span className="text-xs text-muted-foreground bg-muted px-1.5 py-0.5 rounded">
                                        {formatTime(travel.startDate)}
                                    </span>
                                </div>
                                <div className="flex items-center gap-3">
                                    <div className="flex items-center gap-1.5 text-xs text-muted-foreground w-12">
                                        <Clock className="h-3 w-3" /> Fin
                                    </div>
                                    <span className="text-sm font-medium">{formatDate(travel.endDate)}</span>
                                    <span className="text-xs text-muted-foreground bg-muted px-1.5 py-0.5 rounded">
                                        {formatTime(travel.endDate)}
                                    </span>
                                </div>
                            </div>
                        </CardContent>

                        <CardContent className="px-6 py-5">
                            <div className="flex items-center gap-2 mb-3">
                                <MapPin className="h-4 w-4 text-primary" />
                                <p className="text-xs font-semibold text-muted-foreground uppercase tracking-widest">Destino</p>
                                <span className="text-xs bg-muted text-muted-foreground px-2 py-0.5 rounded-full">
                                    {travel.isInternational ? 'Internacional' : 'Nacional'}
                                </span>
                            </div>
                            <p className="text-sm font-medium">
                                {travel.isInternational
                                    ? travel.countries?.map(getCountryLabel).join(', ') || 'No especificado'
                                    : travel.provinces?.map(getProvinceLabel).join(', ') || 'No especificado'}
                            </p>
                            {travel.address && (
                                <p className="text-xs text-muted-foreground mt-1.5">{travel.address}</p>
                            )}
                        </CardContent>
                    </div>

                    {/* Transporte + Costo */}
                    {(transport || travel.estimatedCost) && (
                        <div className="grid grid-cols-1 sm:grid-cols-2 border-b">
                            {transport && (
                                <CardContent className="px-6 py-5 border-b sm:border-b-0 sm:border-r">
                                    <p className="text-xs font-semibold text-muted-foreground uppercase tracking-widest mb-3">
                                        Transporte
                                    </p>
                                    <div className="flex items-center gap-2 text-sm font-medium">
                                        <span className="text-muted-foreground">{transport.icon}</span>
                                        {transport.label}
                                    </div>
                                </CardContent>
                            )}
                            {travel.estimatedCost && (
                                <CardContent className="px-6 py-5">
                                    <p className="text-xs font-semibold text-muted-foreground uppercase tracking-widest mb-3">
                                        Costo estimado
                                    </p>
                                    <div className="flex items-baseline gap-1">
                                        <DollarSign className="h-4 w-4 text-emerald-600 dark:text-emerald-400" />
                                        <span className="text-xl font-bold text-emerald-700 dark:text-emerald-400">
                                            ${travel.estimatedCost.reduce((a, b) => a + b, 0).toFixed(2)} USD
                                        </span>
                                        <span className="text-sm text-muted-foreground">USD</span>
                                    </div>
                                </CardContent>
                            )}
                        </div>
                    )}

                    {/* Empresas */}
                    {travel.companies && travel.companies.length > 0 && (
                        <CardContent className="px-6 py-5 border-b">
                            <div className="flex items-center gap-2 mb-3">
                                <Building2 className="h-4 w-4 text-primary" />
                                <p className="text-xs font-semibold text-muted-foreground uppercase tracking-widest">
                                    Empresas a visitar
                                </p>
                            </div>
                            <div className="flex flex-wrap gap-2">
                                {travel.companies.map((id, idx) => (
                                    <Badge key={idx} variant="secondary" className="gap-1.5 font-normal px-3 py-1.5">
                                        <Building2 className="h-3 w-3" />
                                        {getCompanyName(id)}
                                    </Badge>
                                ))}
                            </div>
                        </CardContent>
                    )}

                    {/* Clientes */}
                    {travel.clients && travel.clients.length > 0 && (
                        <CardContent className="px-6 py-5">
                            <div className="flex items-center gap-2 mb-3">
                                <Users className="h-4 w-4 text-primary" />
                                <p className="text-xs font-semibold text-muted-foreground uppercase tracking-widest">
                                    Clientes
                                </p>
                            </div>
                            <div className="flex flex-wrap gap-2">
                                {travel.clients.map((client, idx) => (
                                    <Badge key={idx} variant="secondary" className="gap-1.5 font-normal px-3 py-1.5">
                                        <Users className="h-3 w-3" />
                                        {client}
                                    </Badge>
                                ))}
                            </div>
                        </CardContent>
                    )}


                    {travel.commentByRrhh && (
                        <div className="px-6 py-5  border-b flex items-start gap-2">
                            <XCircle className="h-4 w-4 text-rose-600 mt-0.5" />
                            <AlertDescription className="text-rose-800 dark:text-rose-200">
                                <span className="font-semibold">Motivo de rechazo: </span>
                                {travel.commentByRrhh}
                            </AlertDescription>
                        </div>
                    )}

                    <CardContent className="px-6 py-4 border-t">
                        <Button asChild size="lg" variant="outline" className="w-full">
                            <Link to="/requests/$id/travels" params={{ id: user?.$id ?? '' }}>
                                <ArrowLeft className="h-4 w-4 mr-2" />
                                Atrás
                            </Link>
                        </Button>
                    </CardContent>

                </Card>
            </div>
        </>
    );
};

export default DetailUserTravel;